import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryClient } from '@/lib/queryClient';
import { useUserInitDailyBonus } from '@/hooks/queries/useUserInitQuery';
import { dailyBonusEvents } from '@/lib/daily-bonus-events';

// ─────────────────────────────────────────────────────────────────────────
// Types (mirroring backend daily-bonus.types.ts)
// ─────────────────────────────────────────────────────────────────────────

export interface TodayRewardStatus {
  canClaim: boolean; // True if today's reward is earned but not claimed
  gc: number; // GC amount (with VIP multiplier)
  sc: number; // SC amount (with VIP multiplier)
}

export interface AmoeDisclosure {
  amoeAvailable: boolean;
  amoeUrl: string;
  message: string;
}

export interface CycleProgressDay {
  day: number;
  gc: number;
  sc: number;
  status: 'claimed' | 'earned' | 'current' | 'upcoming' | 'expired';
}

export interface DailyBonusStatusResponse {
  enabled: boolean;
  streak: {
    currentStreak: number;
    longestStreak: number;
    currentCycleDay: number; // Capped at 7 for day 8+
    lastLoginDate: string | null;
  };
  todayReward: TodayRewardStatus;
  cycleProgress: CycleProgressDay[];
  vipTier: {
    id: number;
    name: string;
    multiplier: number;
    isMaxTier: boolean;
    maxMultiplier: number; // Highest multiplier across all VIP tiers
  };
  disclosure: AmoeDisclosure;
}

export interface DailyBonusClaimResponse {
  success: boolean;
  daysClaimed?: number;
  reward?: {
    gc: number;
    sc: number;
  };
  streak?: {
    currentStreak: number;
    longestStreak: number;
    currentCycleDay: number;
  };
  newBalance?: {
    gc: number;
    scBonus: number;
    scPromotional: number;
    scRedeemable: number;
  };
  error?: 'no_rewards' | 'feature_disabled' | 'rate_limited';
  message?: string;
}

export interface DailyBonusLoginResponse {
  success: boolean;
  enabled: boolean;
  earned: boolean;
  cycleDay: number | null;
  streakBroken: boolean;
  previousStreak: number | null;
  currentStreak: number | null;
}

// ─────────────────────────────────────────────────────────────────────────
// useDailyBonusStatus - Reads daily bonus data from unified /user/init
// ─────────────────────────────────────────────────────────────────────────

export const useDailyBonusStatus = () => {
  const queryClient = useQueryClient();
  const initData = useUserInitDailyBonus();

  // Cast to the full response shape — the init endpoint returns the same structure
  const status = initData as DailyBonusStatusResponse | null;

  // Count earned-but-unclaimed rewards from cycle progress
  const pendingCount = status?.cycleProgress?.filter(d => d.status === 'earned').length ?? 0;

  // Refetch by invalidating the unified init query
  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['user', 'init'] });
  }, [queryClient]);

  return {
    status,
    loading: false,
    error: null,
    refetch,
    // Convenience accessors
    enabled: status?.enabled ?? false,
    canClaim: status?.todayReward?.canClaim ?? false,
    pendingCount,
    currentStreak: status?.streak?.currentStreak ?? 0,
    cycleDay: status?.streak?.currentCycleDay ?? 1,
  };
};

// ─────────────────────────────────────────────────────────────────────────
// useClaimDailyBonus - Mutation hook for claiming daily bonus
// ─────────────────────────────────────────────────────────────────────────

export const useClaimDailyBonus = () => {
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState<DailyBonusClaimResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const claim = useCallback(async (): Promise<DailyBonusClaimResponse> => {
    setClaiming(true);
    setError(null);
    setClaimResult(null);

    try {
      const response = await apiClient.post<DailyBonusClaimResponse>('/daily-bonus/claim');
      setClaimResult(response);

      if (!response.success && response.message) {
        setError(response.message);
      }

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to claim daily bonus';
      setError(errorMessage);

      const errorResponse: DailyBonusClaimResponse = {
        success: false,
        message: errorMessage,
      };
      setClaimResult(errorResponse);
      return errorResponse;
    } finally {
      setClaiming(false);
    }
  }, []);

  const reset = useCallback(() => {
    setClaimResult(null);
    setError(null);
  }, []);

  return {
    claim,
    claiming,
    claimResult,
    error,
    reset,
    // Convenience accessors
    success: claimResult?.success ?? false,
    reward: claimResult?.reward ?? null,
    newBalance: claimResult?.newBalance ?? null,
  };
};

// ─────────────────────────────────────────────────────────────────────────
// processDailyBonusLogin - Called on user login to earn daily reward
// ─────────────────────────────────────────────────────────────────────────

/**
 * Process daily bonus login - call this after successful user authentication.
 * This is NOT a hook - it's a standalone function to be called from AuthContext.
 *
 * This call:
 * - Earns the daily bonus reward for today (if not already earned)
 * - Breaks streak if user missed a day (expires unclaimed rewards)
 * - Is idempotent - safe to call multiple times per day
 *
 * If streak is broken, emits an event via dailyBonusEvents for DailyBonusContext
 * to display a notification to the user.
 *
 * @returns DailyBonusLoginResponse with earned status and streak info
 */
export const processDailyBonusLogin = async (): Promise<DailyBonusLoginResponse> => {
  try {
    const response = await apiClient.post<DailyBonusLoginResponse>('/daily-bonus/login');

    console.log('[processDailyBonusLogin] Response:', response);

    // If streak was broken, emit event for DailyBonusContext to handle
    if (response.streakBroken && response.previousStreak) {
      console.log('[processDailyBonusLogin] Emitting streak broken event:', {
        streakBroken: true,
        previousStreak: response.previousStreak,
      });
      dailyBonusEvents.emitStreakBroken({
        streakBroken: true,
        previousStreak: response.previousStreak,
      });
    }

    // Invalidate user init cache so dailyBonus data refreshes with earned reward
    queryClient.invalidateQueries({ queryKey: ['user', 'init'] });

    return response;
  } catch (err) {
    console.error('Error processing daily bonus login:', err);
    // Return a safe default on error - don't block login flow
    return {
      success: false,
      enabled: false,
      earned: false,
      cycleDay: null,
      streakBroken: false,
      previousStreak: null,
      currentStreak: null,
    };
  }
};
